import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toastApiError } from "@/lib/apiError";

const C = { panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", red: "#F87171", green: "#2CC5A8" };

const STAGE_STYLE = {
  signed_up: { label: "Signed up", bg: "rgba(255,255,255,0.08)", fg: "rgba(255,255,255,0.55)" },
  snapshot: { label: "Snapshot", bg: "rgba(123,211,206,0.15)", fg: C.tealSoft },
  building: { label: "Building", bg: "rgba(245,158,11,0.15)", fg: C.orange },
  exported: { label: "Exported", bg: "rgba(44,197,168,0.15)", fg: C.green },
  retained: { label: "Retained", bg: "rgba(44,197,168,0.25)", fg: C.green },
};
const DOC_LABEL = { pitch: "Pitch deck", fm: "Fin model", valuation: "Valuation", mgmt: "Mgmt accts" };

const fmtD = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
};

const Tile = ({ label, value, sub, accent }) => (
  <div className="rounded-lg px-4 py-3" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</div>
    <div className="text-xl font-extrabold mt-1" style={{ color: accent || "#fff" }}>{value}</div>
    {sub && <div className="text-[10px] text-white/35 mt-0.5">{sub}</div>}
  </div>
);

const Chip = ({ children, bg, fg, testid }) => (
  <span data-testid={testid} className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
    style={{ backgroundColor: bg, color: fg }}>{children}</span>
);

function FunnelTiles({ funnel }) {
  if (!funnel) return null;
  const stages = [
    ["Signed up", funnel.signed_up, null],
    ["Snapshot done", funnel.snapshot_completed, funnel.signed_up],
    ["Started a doc", funnel.started_any_doc, funnel.signed_up],
    ["Exported / finalised", funnel.exported_any, funnel.started_any_doc],
    ["Bought credits", funnel.purchased_credits, funnel.signed_up],
    ["Logged actuals", funnel.logged_actuals, funnel.started_model],
    ["Active (7d)", funnel.active_7d, funnel.signed_up],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="journeys-funnel">
      {stages.map(([label, v, base]) => (
        <Tile key={label} label={label} value={v ?? "—"}
          sub={base ? `${base > 0 ? Math.round(100 * (v || 0) / base) : 0}% of ${base}` : null} />
      ))}
    </div>
  );
}

function DetailDrawer({ userId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/admin/journeys/${userId}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [userId]);
  if (err) return <div className="text-xs text-white/40 p-4">Could not load history.</div>;
  if (!d) return <div className="text-xs text-white/40 p-4">Loading history…</div>;
  const docRows = [
    ...d.docs.pitch_decks.map((x) => ({ kind: "Pitch deck", name: x.company_name, at: x.updated_at, extra: x.exported_at ? `exported ${fmtD(x.exported_at)}` : x.is_public ? "shared" : "" })),
    ...d.docs.fm_models.map((x) => ({ kind: "Fin model", name: x.name, at: x.updated_at, extra: x.export_paid_at ? `export paid ${fmtD(x.export_paid_at)}` : "" })),
    ...d.docs.valuations.map((x) => ({ kind: "Valuation", name: x.company_name, at: x.updated_at, extra: x.finalised_at ? `finalised ${fmtD(x.finalised_at)}` : "" })),
    ...d.docs.mgmt_accounts.map((x) => ({ kind: "Mgmt accts", name: x.company_name, at: x.updated_at, extra: "" })),
  ];
  return (
    <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="journey-detail">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-2">Documents ({docRows.length})</div>
        {docRows.length === 0 && <div className="text-xs text-white/35">None yet.</div>}
        {docRows.map((r, i) => (
          <div key={i} className="text-xs text-white/70 mb-1.5">
            <span className="font-bold text-white">{r.kind}</span> · {r.name || "Untitled"}
            <span className="text-white/40"> · {fmtD(r.at)}{r.extra ? ` · ${r.extra}` : ""}</span>
          </div>
        ))}
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/40 mt-4 mb-2">Emails</div>
        {(d.nudges.length + d.checkins.length) === 0 && <div className="text-xs text-white/35">No nudges or check-ins sent.</div>}
        {d.nudges.map((n, i) => (
          <div key={`n${i}`} className="text-xs text-white/60 mb-1">
            Inactivity nudge{n.day7_sent_at ? ` · e1 ${fmtD(n.day7_sent_at)}` : ""}{n.day14_sent_at ? ` · e2 ${fmtD(n.day14_sent_at)}` : ""}
            {n.outcome ? ` · ${n.outcome}` : ""}
          </div>
        ))}
        {d.checkins.map((c, i) => (
          <div key={`c${i}`} className="text-xs text-white/60 mb-1">Monthly check-in · {c.month || fmtD(c.sent_at)}{c.kind ? ` · ${c.kind}` : ""}</div>
        ))}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-2">Credits ({d.ledger.length} entries)</div>
        {d.ledger.length === 0 && <div className="text-xs text-white/35">No credit activity.</div>}
        <div className="max-h-64 overflow-y-auto pr-2">
          {d.ledger.map((l, i) => (
            <div key={i} className="text-xs mb-1 flex justify-between gap-2">
              <span className="text-white/60 truncate">{l.kind}</span>
              <span className="font-mono font-bold flex-shrink-0" style={{ color: l.amount < 0 ? C.orange : C.green }}>
                {l.amount > 0 ? "+" : ""}{l.amount}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-2">Event timeline (last {d.events.length})</div>
        {d.events.length === 0 && <div className="text-xs text-white/35">No audit events (90-day retention).</div>}
        <div className="max-h-64 overflow-y-auto pr-2">
          {d.events.map((e, i) => (
            <div key={i} className="text-xs text-white/60 mb-1.5">
              <span className="font-mono text-[10px] text-white/35">{fmtD(e.created_at)}</span>{" "}
              <span className="font-semibold text-white/80">{e.event}</span>
              {e.message ? <span className="text-white/40"> — {String(e.message).slice(0, 80)}</span> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function JourneysPanel() {
  const [rows, setRows] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [hideAdmins, setHideAdmins] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/journeys"), api.get("/admin/users/funnel")])
      .then(([j, f]) => { setRows(j.data.journeys || []); setFunnel(f.data); })
      .catch((e) => toastApiError(e, "Could not load journeys"));
  }, []);

  if (!rows) return <div className="text-sm text-white/40 py-8">Loading founder journeys…</div>;
  const visible = rows.filter((r) => !hideAdmins || !r.is_admin);
  const stalled = visible.filter((r) => r.stalled).length;

  return (
    <section data-testid="admin-journeys-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-sm text-white/50 max-w-2xl">
          Every founder's path: signup → snapshot → first document → export → retention.
          Red badges mark founders stalled mid-journey for 7+ days — your nudge list.
        </p>
        <label className="text-xs text-white/50 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={hideAdmins} onChange={(e) => setHideAdmins(e.target.checked)}
            className="accent-[#0A8B85]" data-testid="journeys-hide-admins" />
          Hide admin accounts
        </label>
      </div>

      <FunnelTiles funnel={funnel} />

      <div className="flex items-center gap-4 mb-3 text-xs text-white/45">
        <span><span className="font-bold text-white">{visible.length}</span> founders</span>
        <span><span className="font-bold" style={{ color: C.red }}>{stalled}</span> stalled 7+ days</span>
      </div>

      <div className="rounded-xl overflow-x-auto" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
        <table className="w-full text-xs" data-testid="journeys-table">
          <thead>
            <tr className="text-left text-white/40 uppercase tracking-wider text-[10px] border-b border-white/10">
              <th className="px-4 py-3 font-semibold">Founder</th>
              <th className="px-4 py-3 font-semibold">Signed up</th>
              <th className="px-4 py-3 font-semibold">Snapshot</th>
              <th className="px-4 py-3 font-semibold">First doc</th>
              <th className="px-4 py-3 font-semibold">Docs</th>
              <th className="px-4 py-3 font-semibold">Exported</th>
              <th className="px-4 py-3 font-semibold">Credits</th>
              <th className="px-4 py-3 font-semibold">Last active</th>
              <th className="px-4 py-3 font-semibold">Stage</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <React.Fragment key={r.user_id}>
                <tr onClick={() => setOpenId(openId === r.user_id ? null : r.user_id)}
                  data-testid={`journey-row-${r.user_id}`}
                  className="border-b border-white/5 cursor-pointer hover:bg-white/[0.03] transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-bold text-white">{r.name || "—"}</div>
                    <div className="text-white/40">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmtD(r.signed_up_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.snapshot.completed_at
                      ? <Chip bg="rgba(44,197,168,0.15)" fg={C.green}>✓ {fmtD(r.snapshot.completed_at)}</Chip>
                      : r.snapshot.answered > 0
                        ? <Chip bg="rgba(245,158,11,0.15)" fg={C.orange}>{r.snapshot.answered}/{r.snapshot.total}</Chip>
                        : <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">
                    {r.first_doc ? `${DOC_LABEL[r.first_doc.kind]} · ${fmtD(r.first_doc.at)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">
                    {Object.entries(r.docs).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${DOC_LABEL[k]}`).join(" · ") || "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.exported_at ? <Chip bg="rgba(44,197,168,0.15)" fg={C.green}>✓ {fmtD(r.exported_at)}</Chip>
                      : <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">
                    {r.credits_spent > 0 ? `${r.credits_spent} spent` : "—"}
                    {r.credit_purchases > 0 && <span style={{ color: C.green }}> · {r.credit_purchases} buy{r.credit_purchases > 1 ? "s" : ""}</span>}
                  </td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmtD(r.last_activity)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Chip bg={STAGE_STYLE[r.stage].bg} fg={STAGE_STYLE[r.stage].fg} testid={`journey-stage-${r.user_id}`}>
                      {STAGE_STYLE[r.stage].label}
                    </Chip>
                    {r.stalled && (
                      <Chip bg="rgba(248,113,113,0.15)" fg={C.red} testid={`journey-stalled-${r.user_id}`}>
                        <span className="ml-0">stalled {r.stalled_days}d</span>
                      </Chip>
                    )}
                  </td>
                </tr>
                {openId === r.user_id && (
                  <tr className="border-b border-white/5">
                    <td colSpan={9} style={{ backgroundColor: "rgba(0,0,0,0.25)" }}>
                      <DetailDrawer userId={r.user_id} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
